import { useState } from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup, CircularProgress } from "@mui/material";
import PricingTileNew from "./PricingTileNew";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";

export default function PricingPlans({ planUpgradeType }) {
  const [planPeriod, setPlanPeriod] = useState("monthly");
  const [subscriptionsPlans, setSubscriptionsPlans] = useState([]);

  const handlePeriodChange = (_, newVal) => {
    if (newVal) setPlanPeriod(newVal);
  };

  const subscriptionPlansQuery = useQuery({
    retry: false,
    queryKey: ["subscription-plans"],
    queryFn: fetchPlans,
    onSuccess: (data) => {
      setSubscriptionsPlans(data.response.plans[0].data);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  return (
    <Box sx={{ py: 2 }}>
      {/* Monthly / Yearly toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
        <Box sx={{
          background: "#f1f5f9",
          borderRadius: "50px",
          p: 0.5,
          display: "inline-flex",
        }}>
          <ToggleButtonGroup
            value={planPeriod}
            exclusive
            onChange={handlePeriodChange}
            sx={{
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "50px !important",
                px: 3,
                py: 1,
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "none",
                transition: "all 0.2s",
              },
              "& .Mui-selected": {
                background: "#04343a !important",
                color: "#fff !important",
                boxShadow: "0 4px 12px rgba(4,52,58,0.25)",
                borderRadius: "50px !important",
              },
            }}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly">
              Yearly
              <Box component="span" sx={{
                ml: 1, background: "linear-gradient(135deg,#a3e635,#84cc16)",
                color: "#04343a", fontWeight: 800, fontSize: "0.6rem",
                borderRadius: "10px", px: 0.8, py: 0.2,
              }}>
                20% OFF
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Plan cards */}
      {subscriptionPlansQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#016551" }} />
        </Box>
      ) : subscriptionsPlans.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: "#94a3b8" }}>No plans available.</Typography>
        </Box>
      ) : (
        <Box sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          gap: 3,
          flexWrap: "wrap",
          px: { xs: 2, md: 4 },
          maxWidth: 1100,
          mx: "auto",
        }}>
          {subscriptionsPlans.map((tier) => (
            <PricingTileNew
              key={tier.id}
              tier={tier}
              planPeriod={planPeriod}
              planUpgradeType={planUpgradeType}
            />
          ))}
        </Box>
      )}

      {/* Footer note */}
      <Typography align="center" sx={{ color: "#94a3b8", fontSize: "0.78rem", mt: 4 }}>
        All plans include access to StockYaari platform · Prices in INR · GST extra
      </Typography>
    </Box>
  );
}

// Kept for backward compatibility (used in UserDashboard/SubscriptionPlan)
export const subscriptionPlanTiers = [
  { title: "Free",    subheader: "(with Ads)", price: 0  },
  { title: "Basic",   subheader: "(with Ads)", price: 25 },
  { title: "Premium", subheader: "(No Ads)",   price: 39 },
];

export const fetchPlans = async () => {
  const res = await axiosConfig({
    method: "get",
    url: "subscription/subscription_plan",
    data: {},
  });
  return res.data;
};
